/**
 * normaliseLaunchValues — action-slice normalisation, shared by every launch
 * surface. Moved unchanged out of `LaunchForm.tsx`.
 */

import { decomposeMortgageePrefill } from "~/common/workflows/launch/addressParts";
import { defaultInterestType } from "~/common/workflows/launch/InterestTypeField";
import {
  splitLaunchValues,
  type LaunchBinding,
} from "~/common/workflows/launch/launchSchemaBinding";

export function isEmptyValue(value: unknown): boolean {
  // `""` is NOT empty: it is a deliberate clear by the AM, and refilling it is
  // the "cleared stays cleared" bug this form must never reintroduce.
  return value === undefined || value === null;
}

export function isBlank(value: unknown): boolean {
  return isEmptyValue(value) || (typeof value === "string" && value === "");
}

/**
 * Action-slice normalisation — the ONLY place an action is ever created.
 *
 * The drawer's `values` state is seeded from FLAT prefill and has no action-list
 * key, so without this the first render has no `ActionCard` and the submitted
 * payload is `inputs[actionsKey] = []`: AC-7's "exactly one action" fails, AC-8
 * has nothing to populate, and the must_pass round-trip probe is unsatisfiable.
 *
 * Returns the NEXT values record, or `null` when nothing needs to change — so
 * the caller's effect is a no-op on the steady state and cannot loop.
 *
 * Three rules, in order:
 *
 *  1. **Create** (`actions` empty): take every key of the flat record that
 *     `declaredKeys.action` declares, decompose the record list's addresses into
 *     the four part keys the split State+ZIP line renders, pre-fill the declared
 *     interest type with the contract default, and write ONE action. Keys that
 *     moved into the action and are NOT also declared at run level are removed
 *     from run level — leaving them would make `buildLaunchPayload` report a
 *     dropped key on every single launch, which is how a REAL dropped-key
 *     warning gets ignored.
 *  2. **Fold** (`actions` non-empty): the drawer's shallow merge
 *     (`setValues(prev => ({ ...prev, ...prefill }))`) cannot write into an
 *     action object that already exists, and a child effect can run before the
 *     parent's, so a `[]`-to-one write may land one render before the spread.
 *     Whenever run level gains a declared ACTION key, copy it into the existing
 *     action IFF that action field is still empty (`undefined` / `null` /
 *     absent). Never refill a field the AM typed, never refill one they cleared
 *     (`""`), never add a second action.
 *  3. **Never one-to-two.** `binding.maxActions` is the only gate, and the only
 *     other way an action is rebuilt is RE-ANALYSE success (Phase 4), which
 *     drops `values[actionsKey]` so rule 1 runs again against the fresh brief.
 *
 * `splitLaunchValues` stays total and still never invents an action: reading and
 * creating are deliberately separate, because a `splitLaunchValues` that invented
 * one would also invent it for `handleRetry` and for every unit test that calls
 * it on a half-built record.
 */
export function normaliseLaunchValues(
  binding: LaunchBinding,
  values: Record<string, unknown>,
): Record<string, unknown> | null {
  const actionsKey = binding.actionsKey;
  if (!binding.isReshaped || !actionsKey) return null;

  const { runLevelValues, actions } = splitLaunchValues(binding, values);
  const actionKeys = binding.declaredKeys.action;
  const runKeys = binding.declaredKeys.runLevel;
  const recordsKey = binding.roles.interestRecords;
  const interestTypeKey = binding.roles.interestType;
  const fallbackType = defaultInterestType(binding.interestTypeValues);

  // Declared ACTION keys stranded at run level that the run level does NOT also
  // declare. `old_mortgagee_name` is deliberately unaffected: it is declared at
  // run level, so it is not a stray.
  const strays = Object.keys(runLevelValues).filter(
    (k) => actionKeys.has(k) && !runKeys.has(k),
  );

  if (actions.length === 0) {
    let seed: Record<string, unknown> = {};
    for (const key of Object.keys(runLevelValues)) {
      if (actionKeys.has(key)) seed[key] = runLevelValues[key];
    }
    if (recordsKey) seed = decomposeMortgageePrefill(seed, recordsKey);
    if (interestTypeKey && fallbackType && isBlank(seed[interestTypeKey])) {
      seed[interestTypeKey] = fallbackType;
    }
    const nextRun: Record<string, unknown> = { ...runLevelValues };
    for (const key of strays) delete nextRun[key];
    return { ...nextRun, [actionsKey]: [seed] };
  }

  let changed = strays.length > 0;
  const nextActions = actions.map((action, index) => {
    if (index !== 0) return action;
    const merged: Record<string, unknown> = { ...action };
    let touched = false;
    for (const key of Object.keys(runLevelValues)) {
      if (!actionKeys.has(key)) continue;
      const incoming = runLevelValues[key];
      // An empty incoming value carries no information; writing it would also
      // never settle (null over null re-triggers on every render).
      if (isBlank(incoming)) continue;
      if (!isEmptyValue(merged[key])) continue;
      merged[key] = incoming;
      touched = true;
    }
    if (interestTypeKey && fallbackType && isBlank(merged[interestTypeKey])) {
      merged[interestTypeKey] = fallbackType;
      touched = true;
    }
    if (!touched) return action;
    changed = true;
    return merged;
  });

  if (!changed) return null;
  const nextRun: Record<string, unknown> = { ...runLevelValues };
  for (const key of strays) delete nextRun[key];
  return { ...nextRun, [actionsKey]: nextActions };
}
